import * as yup from 'yup';
import type { StudioCatalog, StudioDefinition, StudioTemplate } from 'src/components/star-grid/reportStudio.types';
import { STUDIO_FORMAT_KINDS, STUDIO_SELECTION_KINDS } from 'src/components/star-grid/reportStudioConstants';
const text = () => yup.string().required();
const strings = () => yup.array().of(text()).defined();
const option = yup.object({ id: text(), label: text() });
const options = () => yup.array().of(option).defined();
const stringMap = () => yup.lazy((value) => yup.object(Object.fromEntries(Object.keys(value ?? {}).map((key) => [key, yup.string().defined()]))));
const parameterMap = () => yup.lazy((value) => yup.object(Object.fromEntries(Object.keys(value ?? {}).map((key) => [key, strings()]))));
const selection = yup.object({
  kind: yup
    .mixed()
    .oneOf([...STUDIO_SELECTION_KINDS])
    .required(),
  members: strings(),
  reference_id: yup.string(),
  include_root: yup.boolean()
});
export const studioFieldSchema = yup.object({ id: text(), binding_id: text(), selection: selection.required() });
const line = yup.object({
  id: text(),
  kind: yup.mixed().oneOf(['member', 'calculation', 'ratio', 'section', 'spacer']).required(),
  label: yup.string().defined(),
  member_id: yup.string(),
  operator: yup.mixed().oneOf(['sum', 'difference', 'ratio']),
  references: yup.array().of(text()).optional(),
  weights: yup.array().of(yup.number().required()),
  format: yup.mixed().oneOf([...STUDIO_FORMAT_KINDS])
});
const lineSet = yup.object({
  id: text(),
  label: text(),
  version: text(),
  binding_id: text(),
  editable: yup.boolean().required(),
  entries: yup.array().of(line).defined()
});
const scenario = yup.object({
  id: text(),
  kind: yup.mixed().oneOf(['scenario']).required(),
  label: yup.string().defined(),
  scenario_id: text(),
  version_id: yup.string().nullable().defined(),
  period_ids: strings(),
  metric_id: text(),
  currency_ids: strings(),
  fx_policy_id: yup.string().nullable().defined()
});
const variance = yup.object({
  id: text(),
  kind: yup.mixed().oneOf(['variance']).required(),
  label: yup.string().defined(),
  base_block_id: text(),
  compare_block_id: text(),
  period_pairs: yup
    .array()
    .of(yup.object({ base: text(), compare: text() }))
    .defined(),
  direction: yup.mixed().oneOf(['base_minus_compare', 'compare_minus_base', 'favorable']).required(),
  fx_policy_id: yup.string().nullable().defined(),
  show_percent: yup.boolean().default(false)
});
const attribute = yup.object({
  id: text(),
  kind: yup.mixed().oneOf(['attribute']).required(),
  label: yup.string().defined(),
  row_instance_id: text(),
  attribute_id: text()
});
const column = yup
  .mixed()
  .defined()
  .test('column', 'Invalid column', function (value) {
    const kind = typeof value === 'object' && value !== null && 'kind' in value ? (value as { kind: unknown }).kind : undefined;
    try {
      (kind === 'scenario' ? scenario : kind === 'variance' ? variance : attribute).validateSync(value, { strict: true });
      return true;
    } catch (error) {
      return this.createError({ message: error instanceof Error ? error.message : 'Invalid column' });
    }
  });
const deck = yup.object({
  rows: yup.array().of(studioFieldSchema).defined(),
  filters: yup.array().of(studioFieldSchema).defined(),
  columns: yup.array().of(column).defined(),
  line_sets: yup.array().of(lineSet).defined()
});
export const studioDefinitionSchema = yup.object({
  schema_version: yup.number().oneOf([1]).required(),
  dataset_id: text(),
  template_id: text(),
  template_version: text(),
  default_version_id: text(),
  active_deck: text(),
  deck_types: strings(),
  decks: yup.lazy((value) => yup.object(Object.fromEntries(Object.keys(value ?? {}).map((key) => [key, deck])))),
  parameters: parameterMap(),
  fixed_context: stringMap(),
  source_mode: yup.mixed().oneOf(['AUTO', 'INGESTED', 'EXTERNAL', 'ICEBERG']).required(),
  suppress_zero_rows: yup.boolean().required()
});
const binding = option.shape({
  dimension: text(),
  hierarchy: text(),
  tree_id: yup.string().nullable(),
  members_path: yup.string().nullable(),
  member_count: yup.number().integer().min(0),
  subsets: options(),
  attributes: options()
});
const dataset = option.shape({
  source: yup.string().optional(),
  bindings: yup.array().of(binding).defined(),
  dimensions: yup
    .array()
    .of(option.shape({ binding_ids: strings() }))
    .optional(),
  selectable_binding_ids: yup.array().of(text()).optional(),
  metrics: yup
    .array()
    .of(
      option.shape({
        format: yup
          .mixed()
          .oneOf([...STUDIO_FORMAT_KINDS])
          .required()
      })
    )
    .defined(),
  currencies: options(),
  fx_policies: options()
});
const catalog = yup.object({
  schema_version: yup.number().oneOf([1]).required(),
  capabilities: yup
    .object({ semantic_preview: yup.boolean().required(), automatic_preview: yup.boolean().required(), filter_sets: yup.boolean().required() })
    .required(),
  datasets: yup.array().of(dataset).defined(),
  templates: yup
    .array()
    .of(option.shape({ dataset_id: text(), definition_version: text(), ready: yup.boolean().required(), unavailable_reasons: strings() }))
    .defined(),
  scenarios: options(),
  versions: yup
    .array()
    .of(option.shape({ as_of_timestamp: yup.string().nullable().defined() }))
    .defined(),
  periods: yup
    .array()
    .of(
      option.shape({
        months: yup.array().of(yup.number().integer().required()).defined(),
        prior_year_id: yup.string(),
        prior_period_id: yup.string()
      })
    )
    .defined()
});
export const parseStudioCatalog = async (value: unknown): Promise<StudioCatalog> =>
  (await catalog.validate(value, { strict: true })) as StudioCatalog;
export const parseStudioDefinition = async (value: unknown): Promise<StudioDefinition> =>
  (await studioDefinitionSchema.validate(value, { strict: true })) as StudioDefinition;
